import React, { useCallback, useState } from 'react';

import { Box, Paper, Stack } from '@mui/material';

import { CodeBaseModel, CodeStoreFileModel, EventHandlerAndPayloadModel, IntegrationEventModel } from '../../api/api';
import PromptChatPanel from '../../components/Chat/PromptChatPanel';
import CreateEventHandlerButton from '../EventHandlerDesigner/Components/CreateEventHandlerButton';
import EventHandlerList from '../EventHandlerDesigner/Components/EventHandlerList';

type DomainEventHandlersViewProps = {
	contextId: string;
	embedded?: boolean;
	selectedHandlerId?: string;
	onSelectHandler?: (handlerId: string) => void;
	handlers: EventHandlerAndPayloadModel[];
	availableEvents: IntegrationEventModel[];
	applicationCodeFiles: CodeStoreFileModel[];
	applicationCodeBase?: CodeBaseModel | null;
	onCreateHandler: (integrationEventId: string, name?: string, description?: string) => Promise<void>;
	onUpdateHandler: (eventHandlerId: string, name: string, description: string, tags: string[]) => Promise<unknown>;
	onDeleteHandler: (eventHandlerId: string) => Promise<unknown>;
	onRefreshCodeArtifacts: () => Promise<void>;
};

const DomainEventHandlersView: React.FC<DomainEventHandlersViewProps> = ({
	contextId,
	embedded,
	selectedHandlerId: controlledSelectedHandlerId,
	onSelectHandler,
	handlers,
	availableEvents,
	applicationCodeFiles,
	applicationCodeBase,
	onCreateHandler,
	onUpdateHandler,
	onDeleteHandler,
	onRefreshCodeArtifacts,
}) => {
	const [selectedHandlerId, setSelectedHandlerId] = useState<string | undefined>(undefined);
	const activeHandlerId = controlledSelectedHandlerId ?? selectedHandlerId;

	const handleRefreshCodeFile = useCallback(
		async (_fileName?: string) => {
			try {
				await onRefreshCodeArtifacts();
			} catch (error) {
				console.error('Error refreshing code file:', error);
			}
		},
		[onRefreshCodeArtifacts],
	);

	return (
		<Stack direction="column" sx={{ flexGrow: 1, minHeight: 0, overflow: 'hidden' }}>
			<Stack
				direction="row"
				sx={{
					flex: 1,
					minHeight: 0,
					overflow: 'hidden',
					gap: (theme) => `${theme.layout.panelGap}px`,
					px: (theme) => `${theme.layout.edgeGap}px`,
					py: (theme) => `${theme.layout.edgeGap}px`,
				}}
			>
				<Paper sx={{ flex: 1, overflow: 'auto', p: 2, position: 'relative' }}>
					{!embedded && (
						<Box mb={2}>
							<CreateEventHandlerButton availableEvents={availableEvents} onCreateHandler={onCreateHandler} />
						</Box>
					)}
					<EventHandlerList
						applicationCodeFiles={applicationCodeFiles}
						applicationCodeBase={applicationCodeBase}
						handlers={handlers}
						onRefreshCodeFile={handleRefreshCodeFile}
						onSelectHandler={(handlerId) => {
							setSelectedHandlerId(handlerId);
							onSelectHandler?.(handlerId);
						}}
						onUpdateHandler={onUpdateHandler}
						onDeleteHandler={onDeleteHandler}
						selectedHandlerId={activeHandlerId}
					/>
				</Paper>
				{!embedded && (
					<PromptChatPanel
						contextId={contextId}
						targetType={activeHandlerId ? 'EventHandler' : undefined}
						targetId={activeHandlerId}
						onComplete={handleRefreshCodeFile}
						onAssistantMessageReceived={() => handleRefreshCodeFile()}
					/>
				)}
			</Stack>
		</Stack>
	);
};

export default DomainEventHandlersView;
